/**
 * API client for survey validation, birthday verification, and submission.
 * @agentic members
 */

import type { Questionnaire } from "@medplum/fhirtypes";

const API_BASE = import.meta.env["VITE_API_URL"] || window.location.origin;

type ValidateResponse = {
  requiresBirthdayVerification: boolean;
  questionnaireCount: number;
  checkInNumber?: number;
  advocateDisplayName?: string;
  advocateTitle?: string;
  advocateMessage?: string;
};

type VerifyBirthdayResponse = {
  verified: boolean;
  // Disclosed only after birthday verification succeeds (BH-SEC-004): the
  // validate endpoint no longer returns patient identity pre-verification.
  patientDisplayName: string;
  /** Formatted SMS number for the patient's care team, when available. */
  careTeamPhoneDisplay?: string;
  questionnaires: Questionnaire[];
  questionnaireUrls: string[];
  token: string;
};

type SubmitResponse = {
  created: Array<{ questionnaireUrl: string; id: string }>;
  status: string;
};

/** Discriminator for token-validation failures surfaced by the survey API. */
export type SurveyErrorReason =
  | "expired"
  | "invalid"
  /** The screener administration is closed (D11) — terminal, same screen as expired. */
  | "closed"
  /** The target instrument/section is explicitly settled (declined) — re-sync state. */
  | "settled"
  /** The administration/section is already complete — re-sync state. */
  | "completed"
  /** Transient server condition — safe to retry the same request. */
  | "retry";

type ErrorResponse = {
  error: string;
  alreadyCompleted?: boolean;
  reason?: SurveyErrorReason;
};

/**
 * Parse an error response, handling both JSON and plain-text bodies.
 */
const parseErrorResponse = async (res: Response): Promise<SurveyError> => {
  const text = await res.text().catch(() => "");
  try {
    const body = JSON.parse(text) as ErrorResponse;
    return new SurveyError(
      body.error,
      body.alreadyCompleted ?? false,
      body.reason,
    );
  } catch {
    return new SurveyError(text || `Request failed (${res.status})`, false);
  }
};

/**
 * Validate a survey token. Returns the birthday-verification flag and survey
 * metadata; patient identity is withheld until verification (BH-SEC-004).
 * @agentic members
 */
export const validateSurveyToken = async (
  token: string,
): Promise<ValidateResponse> => {
  const res = await fetch(
    `${API_BASE}/surveys/validate?token=${encodeURIComponent(token)}`,
  );

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json() as Promise<ValidateResponse>;
};

/**
 * Verify patient identity via birth date. Returns questionnaire data on success.
 * @agentic members
 */
export const verifyBirthday = async (
  token: string,
  birthDate: string,
): Promise<VerifyBirthdayResponse> => {
  const res = await fetch(`${API_BASE}/surveys/verify-birthday`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, birthDate }),
  });

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json() as Promise<VerifyBirthdayResponse>;
};

type SurveyResponseItem = {
  linkId: string;
  text?: string;
  answer?: Array<{
    valueInteger?: number;
    valueString?: string;
    valueCoding?: { code: string; display: string };
    valueBoolean?: boolean;
  }>;
};

/**
 * Submit survey answers for one or more questionnaires.
 * @agentic members
 */
export const submitSurvey = async (
  token: string,
  responses: Array<{
    questionnaireUrl: string;
    items: SurveyResponseItem[];
  }>,
): Promise<SubmitResponse> => {
  const res = await fetch(`${API_BASE}/surveys/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, responses }),
  });

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json() as Promise<SubmitResponse>;
};

/**
 * Error class with alreadyCompleted flag for deduplication handling.
 * @agentic members
 */
export class SurveyError extends Error {
  constructor(
    message: string,
    public readonly alreadyCompleted: boolean,
    public readonly reason?: SurveyErrorReason,
  ) {
    super(message);
    this.name = "SurveyError";
  }
}

type NpsValidateResponse = {
  questionnaire: Questionnaire;
  questionnaireUrl: string;
  welcomeTitle: string;
  welcomeBody: string;
  thankYouMessage: string;
};

type NpsSubmitResponse = {
  questionnaireResponseId: string;
  status: string;
  thankYouMessage: string;
};

/**
 * Validate an NPS session token and load the survey definition.
 * @agentic members
 */
export const validateNpsSurvey = async (
  token: string,
): Promise<NpsValidateResponse> => {
  const res = await fetch(
    `${API_BASE}/surveys/nps/validate?token=${encodeURIComponent(token)}`,
  );

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json() as Promise<NpsValidateResponse>;
};

/**
 * Submit anonymous NPS survey answers.
 * @agentic members
 */
export const submitNpsSurvey = async (
  token: string,
  items: SurveyResponseItem[],
): Promise<NpsSubmitResponse> => {
  const res = await fetch(`${API_BASE}/surveys/nps/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, items }),
  });

  if (!res.ok) {
    throw await parseErrorResponse(res);
  }

  return res.json() as Promise<NpsSubmitResponse>;
};
